'use strict';

// Dependencies

const $ = require('dibs-jquery-shim');
const SoaModel = require('bunsen/models/SoaModel');
const Publication = module.exports = SoaModel.extend();
const Backbone = require('dibs-backbone');
const PublicationType = require('bunsen/models/inventory/PublicationType');
const NotesDocumentList = require('bunsen/models/NotesDocumentList');
const ListingSearch = require('bunsen/models/inventory/ListingSearch');
const endpoints = require('dibs-endpoints');
const PublicationAttributeWrappers = require('bunsen/collections/inventory/PublicationAttributeWrappers');
const PublicationAttribute = require('bunsen/models/inventory/PublicationAttribute');
const ends = require('dibs-endpoints');
const AvailableActions = require('bunsen/collections/AvailableActions');
const moment = require('moment');
const AuthorizedRoles = require('bunsen/collections/identity/AuthorizedRoles');
const assert = require('bunsen/helpers/assert');
const _ = require('underscore');
const requ = require('reku');
const transitionMixin = require('bunsen/models/mixins/transitionMixin');
const YES = 'Y';
const bulkType = 'ATTACH_ELIGIBLE_LISTINGS';
let actionNames;
let listingRelations;
const STATUS = {
    NEW: 'NEW',
    PENDING: 'PENDING',
    COMPLETE: 'COMPLETE',
    PUBLISHED: 'PUBLISHED',
    DELETED: 'DELETED',
    UNAVAILABLE: 'UNAVAILABLE'
};
const REASON_CODE = {
    MANUALLY_DELETED: 'MANUALLY_DELETED'
};
const markStatus = function (status) {
    return function () {
        return this.set('status', status);
    };
};

/**
 * @class Publication
 * @extends SoaModel
 */

_.extend(Publication.prototype, {

    url : function () {
        return ends.publication(this.get('id'));
    },

    relations: [
        {
            type: Backbone.One,
            key: 'notes',
            relatedModel: NotesDocumentList
        },
        {
            type: Backbone.One,
            key: 'type',
            relatedModel: PublicationType
        },
        {
            type: Backbone.Many,
            key: 'attributeValues',
            collectionType: PublicationAttributeWrappers
        },
        {
            type: Backbone.Many,
            key: 'actions',
            collectionType: AvailableActions
        }
    ],

    /**
     * Returns true if publication's status is new, false otherwise.
     * @returns {boolean}
     */
    isNewStatus: function () {
        return this.get('status') === Publication.STATUS.NEW;
    },

    /**
     * Returns true if the publication's status is pending, false otherwise.
     * @returns {boolean}
     */
    isPendingStatus: function () {
        return this.get('status') === Publication.STATUS.PENDING;
    },

    /**
     * Returns true if the publication's status is completed, false otherwise.
     * @returns {boolean}
     */
    isCompleteStatus: function () {
        return this.get('status') === Publication.STATUS.COMPLETE;
    },

    /**
     * Returns true if the publication's status is published, false otherwise.
     * @returns {boolean}
     */
    isPublishedStatus: function () {
        return this.get('status') === Publication.STATUS.PUBLISHED;
    },

    /**
     * Returns true if the publication's status is deleted, false otherwise.
     * @returns {boolean}
     */
    isDeletedStatus: function () {
        return this.get('status') === Publication.STATUS.DELETED;
    },

    /**
     * Returns true if submissions deadline has passed, false otherwise.
     * @returns {boolean}
     */
    isPassedDeadline: function () {
        return moment().isAfter(this.get('submissionsDate'));
    },

    /**
     * Returns true if all listings in the publication have completed all required reviews, false otherwise.
     * @returns {boolean}
     */
    hasCompletedReviews: function () {
        return this.get('reviewsCompleted') === YES;
    },

    /**
     * Returns true if the user has permission to merchandise the publication, false otherwise.
     * @param user User
     * @returns {boolean}
     */
    canBeMadeMerchandiseable: function (user) {
        let merchandiseWrapper;
        let roles;

        // if this publication is pending or published, retrieve the corresponding merchandise attribute, if any
        if (this.canMarkPending(user)) {
            merchandiseWrapper = this.get('attributeValues').getMerchandisePending();
        } else if (this.canMarkPublished(user)) {
            merchandiseWrapper = this.get('attributeValues').getMerchandisePublished();
        }

        // return true if the current user has any of the provided permissions OR if the current user matches
        // any of the whitelisted user id's
        if (merchandiseWrapper) {
            roles = new AuthorizedRoles(merchandiseWrapper.getValues().models);
            return roles.isUserPermitted(user);
        }

        return false;
    },

    getAttributeValues: function () {
        return this.get('attributeValues');
    },

    canPromoteOrDemote: function () {
        var attr = this.getAttributeValues().getAttribute(PublicationAttribute.DISPLAY_PROMOTE_DEMOTE);
        if (attr) {
            return attr.getValues().at(0).get('data');
        }
        return false;
    },

    /**
     * Returns true if the publication is currently merchandisable by the current user, false otherwise.
     * @param {User} user
     * @returns {boolean}
     */
    isMerchandisable: function (user) {
        return !!(this.get('actions') &&
            this.get('actions').isUserAuthorizedFor(Publication.ACTION_NAMES.MERCHANDISE_PUBLICATION, user));
    },

    ////////////////////////////
    // actions (state transitions)
    /**
     * Returns true if the user has permission to move the publication to pending, false otherwise.
     * @param {User} user
     * @returns {boolean}
     */
    canMarkPending: function (user) {
        return !!(this.get('actions') &&
            this.get('actions').isUserAuthorizedFor(Publication.ACTION_NAMES.PENDING, user));
    },
    /**
     * Returns true if the user has permission to move the publication to published, false otherwise.
     * @param {User} user
     * @returns {boolean}
     */
    canMarkPublished: function (user) {
        return !!(this.get('actions') &&
            this.get('actions').isUserAuthorizedFor(Publication.ACTION_NAMES.PUBLISHED, user));
    },
    /**
     * Returns true if the user has permission to move the publication to complete, false otherwise.
     * @param {User} user
     * @returns {boolean}
     */
    canMarkComplete: function (user) {
        return !!(this.get('actions') &&
            this.get('actions').isUserAuthorizedFor(Publication.ACTION_NAMES.COMPLETE, user));
    },
    /**
     * Returns true if the user has permission to delete the publication, false otherwise.
     * @param {User} user
     * @returns {boolean}
     */
    canMarkDeleted: function (user) {
        return !!(this.get('actions') &&
            this.get('actions').isUserAuthorizedFor(Publication.ACTION_NAMES.DELETED, user));
    },

    /**
     * Returns true if the publication can be deleted (independent of user), false otherwise.
     * @returns {boolean}
     */
    isDeletable: function () {
        return !!(this.get('actions') && this.get('actions').findWhere({code : Publication.ACTION_NAMES.DELETED}));
    },

    /**
     * Get the verticals of this publication from the attributes
     * @returns {String|Array} Array of verticals or empty array if none found
     */
    getVerticals: function () {
        let category;
        if (this.get('attributeValues')) {
            category = this.get('attributeValues').getCategory();
            return (category && category.getValues().pluck('label')) || [];
        }

        return [];
    },

    getNotes: function () {
        let notes = this.get('notes');

        if (_.isUndefined(notes)) {
            this.set('notes', new NotesDocumentList({documents: []}));
            notes = this.get('notes');
        }
        return notes;
    },

    /**
     * Returns true if all listings in the publication have completed all required reviews, false otherwise.
     * @returns undefined
     */
    markPendingStatus: markStatus(STATUS.PENDING),
    markNewStatus: markStatus(STATUS.NEW),
    markCompleteStatus: markStatus(STATUS.COMPLETE),
    markPublishedStatus: markStatus(STATUS.PUBLISHED),
    markDeletedStatus: markStatus(STATUS.DELETED),

    /**
     * Returns the count of additional listings that were submitted before the deadline
     * that can be attached to the current publication.
     * @param options
     *   - relations: {Array} - specify if you want attached, eligible, or attachable listings (or some combination)
     *     - attached listings means status QUEUED, pubId set to current pub
     *     - eligible listings means status NEW OR COMPLETE, no pubId or pubId set to current pub
     *     - attachable listings means status COMPLETED, no pubId
     * @returns {SolrSearchModel}
     */
    getNumListingsSearch: function (options) {
        let relations;
        let listingSearch;
        options = options || {};
        relations = _.isArray(options.relations) ? options.relations : [];

        listingSearch = new ListingSearch(null, {
            publicationId: this.get('id'),
            availableActions: '',
            baseParams: ['publicationId', 'availableActions']
        });

        listingSearch.setParam('pageSize', 0); // we don't want listings, just count (comes back as totalResults)
        listingSearch.setParam('facets', false); // for performance - don't need facets calculated

        // set relations passed in
        _.each(relations, function (relation) {
            listingSearch.setParam('publicationRelation', relation, {append: true});
        });

        return listingSearch;
    },

    /**
     * Bulk attach listings to the publication.
     * @param options Save options
     * @returns {$.Deferred.promise}
     */
    bulkAttachListings: function (options) {
        const request = new requ.Request();
        const params = this.getTokenObj();
        let data;
        params.indexMode = 'sync';
        data = {
            "apiType" : "publicationProcessingRequest",
            "type" : bulkType
        };
        options = options || {};

        return request.send(_.extend({
            type: 'POST',
            url: endpoints.bulkAttachListings(this.get('id')) + "?" + $.param(params),
            contentType: 'application/json; charset=UTF-8',
            data: JSON.stringify(data)
        }, options));
    },

    /**
     * Attach listing to a publication (or move to a different publication)
     * @param listingId - the listing being moved
     * @param options - what callbacks should be passed
     */
    attachListing: function (listingId, options) {
        assert(listingId, 'You must include a listingId to assign to this publication.');
        options = options || {};

        return this.save({
            apiType: 'itemPublication',
            id: this.get('id')
        }, _.extend(options, {
            url: ends.listingAttach(listingId),
            type: 'PUT'
        }));
    },

    getEditLink: function () {
        return '/internal/publication/' + this.get('id');
    },

    setType: function (type) {
        return this.set('type', type);
    },
    getType: function () {
        return this.get('type');
    },

    setName: function (name) {
        return this.set('name', name);
    },

    /**
     * Returns true if the publication has the TEST_PUBLICATION attribute and the value of that attribute is true
     */
    isTestPublication() {
        const testPubAttr = this.get('attributeValues').getAttribute('TEST_PUBLICATION');
        return testPubAttr && testPubAttr.getValues().findWhere({ data: true });
    }
}, transitionMixin, {
    /**
     * Adds a check to the transitionMixin#save method to see if the publication should be made into a Test Publication
     * If so, SoaModel#save is called on an empty model (no unnecessary attrs) using the makeTestPublication endpoint
     */
    save(attrs, options) {
        const makeTestPub = this.get('testPublication');
        if (makeTestPub) {
            this.unset('testPublication');
        }
        return transitionMixin.save.call(this, attrs, options)
            .done(() => {
                if (makeTestPub) {
                    return (new SoaModel()).save({
                        type: 'MAKE_TEST_PUBLICATION'
                    }, {
                        url: endpoints.makeTestPublication(this.get('id')),
                        type: 'POST'
                    });
                }
            });
    }
});


actionNames = {
    ATTACH_ELIGIBLE_LISTINGS : 'ATTACH_ELIGIBLE_LISTINGS',
    COPY_SORT_ORDER : 'COPY_SORT_ORDER',
    REMOVE_LISTINGS : 'REMOVE_LISTINGS',
    MERCHANDISE_PUBLICATION : 'MERCHANDISE_PUBLICATION'
};
listingRelations = {
    ATTACHED : 'attached',
    ELIGIBLE : 'eligible',
    ATTACHABLE: 'attachable'
};

_.extend(actionNames, STATUS);
Object.freeze(actionNames);
_.extend(Publication, {
    ACTION_NAMES: actionNames,
    LISTING_RELATIONS: listingRelations,
    STATUS: STATUS,
    REASON_CODE: REASON_CODE
});



// WEBPACK FOOTER //
// ./~/bunsen/models/inventory/Publication.es.js